import { useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Skeleton from 'react-loading-skeleton';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import useUploadFiles from 'api/hooks/useUploadFiles';
import type { UploadHubListItem } from 'api/hooks/useUploadFiles';
import UploadHubModalAttach from 'components/UploadHub/UploadHubModalAttach';
import type { AttachedFile } from 'components/UploadHub/UploadHubModalAttach';
import { getFileBadge } from 'components/UploadHub/fileTypeBadges';
import { validateUploadFile } from 'utils/uploadFileValidation';
import { buildDocumentSharePageV2Url } from 'utils/buildShareDocumentV2Link';

interface InteractionAttachmentsProps {
  attachments?: string[];
  onChange: (attachments: string[]) => void;
  disabled?: boolean;
}

const InteractionAttachments = ({ attachments = [], onChange, disabled }: InteractionAttachmentsProps) => {
  const { getUploadHubList } = useUploadFiles();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dropFile, setDropFile] = useState<File | null>(null);
  const [scrollTarget, setScrollTarget] = useState<string | null>(null);
  const attachmentItems = useRef<Record<string, HTMLLIElement | null>>({});
  const [filesByUrl, setFilesByUrl] = useState<Record<string, AttachedFile>>({});
  const [isLoadingFiles, setIsLoadingFiles] = useState(attachments.length > 0);

  const fetchFiles = useRef<((signal: AbortSignal) => Promise<void>) | null>(null);
  fetchFiles.current = async (signal) => {
    try {
      const results: UploadHubListItem[] = await getUploadHubList({ signal });
      const teamFiles = Object.fromEntries(
        (results ?? []).map(({ publicId, fileName, contentType }) => {
          const url = buildDocumentSharePageV2Url(publicId);
          return [url, { url, fileName, contentType }];
        })
      );
      setFilesByUrl((current) => ({ ...teamFiles, ...current }));
      setIsLoadingFiles(false);
    } catch {
      // apiClient's error interceptor already shows a toast
    }
  };

  useEffect(() => {
    if (attachments.length === 0) return;
    const controller = new AbortController();
    fetchFiles.current?.(controller.signal);
    return () => controller.abort('Component unmounted.');
  }, []);

  const addAttachments = (files: AttachedFile[]) => {
    setFilesByUrl((current) => ({ ...current, ...Object.fromEntries(files.map((file) => [file.url, file])) }));
    const newUrls = files.map(({ url }) => url).filter((url) => !attachments.includes(url));
    if (newUrls.length > 0) onChange(attachments.concat(newUrls));
    setScrollTarget(files[files.length - 1]?.url ?? null);
  };

  useEffect(() => {
    if (!scrollTarget) return;
    attachmentItems.current[scrollTarget]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    setScrollTarget(null);
  }, [scrollTarget]);

  const openModal = (file: File | null) => {
    setDropFile(file);
    setIsModalOpen(true);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    noClick: true,
    noKeyboard: true,
    validator: validateUploadFile,
    disabled,
    onDrop: (acceptedFiles, fileRejections) => {
      if (acceptedFiles.length > 0) openModal(acceptedFiles[0]);
      fileRejections.forEach(({ file, errors }) => {
        createNotification('danger', `${file.name}: ${errors[0]?.message ?? 'File could not be added'}`);
      });
    },
  });

  return (
    <div className='mb-3'>
      <div className='flex-centered mb-2 '>
        <label className='d-block mr-2 paragraph-p2 font-weight-bold mb-0'>Attachments</label>
      </div>

      {!disabled && (
        <>
          <button
            {...getRootProps({
              type: 'button',
              className: `general-button text-light-blue paragraph-p2 px-3 hovered-button specific-y-padding-8 w-100 border-light-blue box-rounded text-left spedific-padding ${isDragActive ? 'active-dropzone' : ''}`,
              onClick: () => openModal(null),
            })}
          >
            {isDragActive ? 'Drop your file here' : '+ Attach a file'}
          </button>
          <input {...getInputProps()} />
        </>
      )}

      {attachments.length > 0 && (
        <ul className='policy-list px-0 mb-3'>
          {attachments.map((url) => {
            const file = filesByUrl[url];
            if (!file && isLoadingFiles) {
              return (
                <li className='border-main-blue-50 box-rounded my-3 attachment-name flex-centered' key={url}>
                  <Skeleton width={20} height={20} borderRadius={3} className='mr-2' />
                  <Skeleton width={180} height={14} />
                </li>
              );
            }
            const badge = getFileBadge({ contentType: file?.contentType });
            const fileName = file?.fileName ?? 'File';
            return (
              <li
                className='border-main-blue-50 box-rounded d-flex justify-content-between my-3 align-items-center'
                key={url}
                style={{ scrollMarginBottom: '20px' }}
                ref={(element) => {
                  attachmentItems.current[url] = element;
                }}
              >
                <a
                  href={url}
                  target='_blank'
                  rel='noreferrer'
                  className='attachment-name mb-0 flex-centered text-main-blue general-link'
                >
                  <span
                    className={`mr-2 font-weight-bold upload-hub-file-icon upload-hub-file-icon-sm ${badge.className} text-white text-center flex-centered justify-content-center`}
                  >
                    {badge.label}
                  </span>
                  <span className='hidden-lines hidden-one-line'>{fileName}</span>
                </a>
                {!disabled && (
                  <div className='px-3'>
                    <button
                      type='button'
                      className='general-button icon-delete upload-hub-remove-file'
                      aria-label='Remove attachment'
                      data-tooltip-id='general-tooltip'
                      data-tooltip-content='Remove'
                      onClick={() => onChange(attachments.filter((attachment) => attachment !== url))}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <UploadHubModalAttach
        openState={{ isOpen: isModalOpen, setIsOpen: setIsModalOpen }}
        onAttach={addAttachments}
        initialFile={dropFile}
      />
    </div>
  );
};

export default InteractionAttachments;
